import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Grid, Button, TextField, MenuItem, Stack, Chip, Avatar, IconButton,
    Snackbar, Alert, Tooltip, LinearProgress, InputAdornment, CircularProgress,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ContactEmergencyRoundedIcon from '@mui/icons-material/ContactEmergencyRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addEmployee } from '../redux/slices/employeesSlice';
import useOrgOptions from '../hooks/useOrgOptions';
import http, { apiErrorMessage } from '../Api/http';
import { PostEmployee, SetEmployeeLoginPassword, UpdateEmployeeUserType, GetEmployeeById, GetUserTypes, GetLoginIdFormat, UploadEmployeeDoc } from '../Api/Api';
import { selectUserTypeId } from '../redux/slices/authSlice';
import { canAssignRole, ROLE } from '../data/roleAccess';
import { toApiDate, numOrNull, txt } from '../utils/apiFields';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_BORDER = '#B3D3E8';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#D0E5F3' } };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: `0 2px 6px ${PRIMARY}40`, textTransform: 'none', '&:hover': { bgcolor: '#0E4A75', boxShadow: `0 4px 10px ${PRIMARY}55` } };
const savingBtn = { '&.Mui-disabled': { bgcolor: PRIMARY, color: '#fff', opacity: 0.7, boxShadow: 'none' } };
const field = { '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 14, bgcolor: '#F8FAFC', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#D8DEE8' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } } };

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed'];
const BLOOD = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const EMP_TYPES = ['Permanent', 'Probation', 'Contract', 'Internship', 'Part-Time'];
const DOC_ITEMS = [
    { label: 'Aadhaar Card', slug: 'aadhaar-card' },
    { label: 'PAN Card', slug: 'pan-card' },
    { label: 'Passport', slug: 'passport' },
    { label: 'Resume', slug: 'resume' },
    { label: 'Educational Certificates', slug: 'educational-certificates' },
    { label: 'Experience Certificates', slug: 'experience-certificates' },
    { label: 'Offer Letter', slug: 'offer-letter' },
    { label: 'Appointment Letter', slug: 'appointment-letter' },
    { label: 'Joining Letter', slug: 'joining-letter' },
    { label: 'Relieving Letter', slug: 'relieving-letter' },
    { label: 'Bank Passbook / Cancelled Cheque', slug: 'bank-passbook' },
    { label: 'Address Proof', slug: 'address-proof' },
    { label: 'Signature', slug: 'signature' },
    { label: 'Other Documents', slug: 'other' },
];

const SECTIONS = [
    { id: 'login', label: 'Login Credentials', icon: LockRoundedIcon, required: true },
    { id: 'personal', label: 'Personal Information', icon: PersonRoundedIcon, required: true },
    { id: 'employment', label: 'Employment Information', icon: WorkRoundedIcon, required: true },
    { id: 'contact', label: 'Contact Information', icon: HomeRoundedIcon },
    { id: 'identity', label: 'Government & Identity', icon: BadgeRoundedIcon },
    { id: 'education', label: 'Education', icon: SchoolRoundedIcon },
    { id: 'previous', label: 'Previous Employment', icon: HistoryRoundedIcon },
    { id: 'emergency', label: 'Emergency Contact', icon: ContactEmergencyRoundedIcon },
    { id: 'bank', label: 'Bank Details', icon: AccountBalanceRoundedIcon },
    { id: 'documents', label: 'Documents Upload', icon: FolderRoundedIcon },
];

const MANDATORY = [
    { key: 'password', label: 'Password' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'gender', label: 'Gender' },
    { key: 'dob', label: 'Date of Birth' },
    { key: 'personalMobile', label: 'Personal Mobile' },
    { key: 'dateOfJoining', label: 'Date of Joining' },
    { key: 'employmentType', label: 'Employment Type' },
    { key: 'department', label: 'Department' },
    { key: 'designation', label: 'Designation' },
];

const todayInput = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MAX_PREV_JOBS = 10;
const emptyPrevJob = () => ({ company: '', designation: '', experience: '', startDate: '', endDate: '', lastDrawnSalary: '', reasonForLeaving: '' });

const MAX_EDU = 5;
const EDU_LEVELS = ['10th / SSLC', '12th / HSC', 'Diploma', 'Undergraduate (UG)', 'Postgraduate (PG)', 'Doctorate (PhD)', 'Other'];
const emptyEducation = () => ({ level: '', degree: '', specialization: '', institution: '', board: '', yearOfPassing: '', percentage: '' });

const EMPTY = {
    password: '', confirmPassword: '', userTypeId: ROLE.EMPLOYEE,
    companyEmployeeId: '',
    firstName: '', lastName: '', gender: '', dob: '', maritalStatus: '', bloodGroup: '', nationality: '',
    personalEmail: '', personalMobile: '', alternateMobile: '',
    dateOfJoining: '', employmentType: '', department: '', designation: '', probationPeriod: '', confirmationDate: '',
    presentAddressLine1: '', presentAddressLine2: '', presentCity: '', presentState: '', presentCountry: '', presentPostalCode: '',
    sameAsPresent: false,
    permanentAddressLine1: '', permanentAddressLine2: '', permanentCity: '', permanentState: '', permanentCountry: '', permanentPostalCode: '',
    aadhaar: '', pan: '', passport: '', drivingLicense: '', voterId: '', uan: '', esi: '', pf: '',
    education: [emptyEducation()], certifications: '',
    previousEmployments: [emptyPrevJob()],
    emergencyName: '', emergencyRelationship: '', emergencyMobile: '', emergencyAlternate: '', emergencyAddress: '',
    accountHolderName: '', bankName: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', branch: '',
};

const COMPANY_ID_RE = /^[A-Za-z0-9._-]{3,20}$/;

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_RE = /^\d{9,18}$/;

const buildEmployeePayload = (form) => ({
    companyEmployeeId: txt(form.companyEmployeeId) ? txt(form.companyEmployeeId).toUpperCase() : null,
    firstName: txt(form.firstName),
    lastName: txt(form.lastName),
    gender: txt(form.gender),
    dateOfBirth: toApiDate(form.dob),
    maritalStatus: txt(form.maritalStatus),
    bloodGroup: txt(form.bloodGroup),
    nationality: txt(form.nationality),

    personalEmail: txt(form.personalEmail),
    personalMobile: txt(form.personalMobile),
    alternateMobile: txt(form.alternateMobile),

    employmentType: txt(form.employmentType),
    dateOfJoining: toApiDate(form.dateOfJoining),
    department: txt(form.department),
    designation: txt(form.designation),
    probationPeriodMonths: numOrNull(form.probationPeriod),
    confirmationDate: toApiDate(form.confirmationDate),

    ...(() => {
        const present = {
            line1: txt(form.presentAddressLine1), line2: txt(form.presentAddressLine2),
            city: txt(form.presentCity), state: txt(form.presentState),
            country: txt(form.presentCountry), postalCode: txt(form.presentPostalCode),
        };
        const permanent = form.sameAsPresent ? { ...present } : {
            line1: txt(form.permanentAddressLine1), line2: txt(form.permanentAddressLine2),
            city: txt(form.permanentCity), state: txt(form.permanentState),
            country: txt(form.permanentCountry), postalCode: txt(form.permanentPostalCode),
        };
        return { presentAddress: present, permanentAddress: permanent };
    })(),

    aadhaarNumber: txt(form.aadhaar),
    panNumber: txt(form.pan),
    passportNumber: txt(form.passport),
    drivingLicense: txt(form.drivingLicense),
    voterId: txt(form.voterId),
    uanNumber: txt(form.uan),
    esiNumber: txt(form.esi),
    pfNumber: txt(form.pf),

    educations: (Array.isArray(form.education) ? form.education : [])
        .map((e) => ({
            qualification: txt(e.level),
            courseOrSpecialization: [txt(e.degree), txt(e.specialization)].filter(Boolean).join(' — '),
            institution: txt(e.institution),
            boardOrUniversity: txt(e.board),
            yearOfPassing: numOrNull(e.yearOfPassing),
            percentageOrCgpa: txt(e.percentage),
        }))
        .filter((e) => e.qualification || e.courseOrSpecialization || e.institution
            || e.boardOrUniversity || e.yearOfPassing != null || e.percentageOrCgpa),
    certifications: txt(form.certifications),

    previousEmployments: (Array.isArray(form.previousEmployments) ? form.previousEmployments : [])
        .map((p) => ({
            companyName: txt(p.company),
            designation: txt(p.designation),
            experienceYears: numOrNull(p.experience),
            startDate: toApiDate(p.startDate),
            endDate: toApiDate(p.endDate),
            lastDrawnSalary: numOrNull(p.lastDrawnSalary),
            reasonForLeaving: txt(p.reasonForLeaving),
        }))
        .filter((p) => p.companyName || p.designation || p.experienceYears != null
            || p.startDate || p.endDate || p.lastDrawnSalary != null || p.reasonForLeaving),

    emergencyContactName: txt(form.emergencyName),
    emergencyRelationship: txt(form.emergencyRelationship),
    emergencyMobile: txt(form.emergencyMobile),
    emergencyAlternateNumber: txt(form.emergencyAlternate),
    emergencyAddress: txt(form.emergencyAddress),

    accountHolderName: txt(form.accountHolderName),
    bankName: txt(form.bankName),
    accountNumber: txt(form.accountNumber),
    ifsc: txt(form.ifsc) ? txt(form.ifsc).toUpperCase() : '',
    branch: txt(form.branch),
});

const Label = ({ children, req }) => (
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>
        {children}{req && <Box component="span" sx={{ color: '#E11D48', ml: 0.4 }}>*</Box>}
    </Typography>
);

function Field({ label, req, value, onChange, type, placeholder, error, min, max, helperText }) {
    const dateProps = type === 'date' ? { inputLabel: { shrink: true } } : undefined;
    const htmlInput = (min || max) ? { htmlInput: { ...(min ? { min } : {}), ...(max ? { max } : {}) } } : undefined;
    return (
        <Box>
            <Label req={req}>{label}</Label>
            <TextField fullWidth size="small" type={type} placeholder={placeholder} value={value} onChange={onChange}
                error={error} helperText={helperText} sx={field}
                slotProps={(dateProps || htmlInput) ? { ...dateProps, ...htmlInput } : undefined} />
        </Box>
    );
}

function Select({ label, req, value, onChange, options, error, emptyText = 'No options to select' }) {
    const empty = !options || options.length === 0;
    return (
        <Box>
            <Label req={req}>{label}</Label>
            <TextField
                select fullWidth size="small"
                value={empty ? '' : value}
                onChange={onChange}
                error={error && !empty}
                disabled={empty}
                sx={field}
                slotProps={{ select: { displayEmpty: true } }}
                helperText={empty ? emptyText : undefined}
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.6, fontSize: 11.5, color: '#94A3B8' } }}
            >
                {empty
                    ? [<MenuItem key="__none" value="" disabled sx={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>{emptyText}</MenuItem>]
                    : options.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 13.5 }}>{o}</MenuItem>)}
            </TextField>
        </Box>
    );
}

function SectionCard({ section, children }) {
    const Icon = section.icon;
    return (
        <Box id={`sec-${section.id}`} sx={{ ...card, p: 0, scrollMarginTop: 90 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.8, borderBottom: '1px solid #F1F3F7' }}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '8px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon sx={{ fontSize: 19, color: PRIMARY }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>{section.label}</Typography>
                </Stack>
                <Chip label={section.required ? 'Required' : 'Optional'} size="small"
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: section.required ? '#FEF2F2' : '#F1F5F9', color: section.required ? '#E11D48' : '#64748B' }} />
            </Stack>
            <Box sx={{ p: 2.5 }}>{children}</Box>
        </Box>
    );
}

export default function OnboardEmployeePage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [empId, setEmpId] = useState('');
    const myUserTypeId = useSelector(selectUserTypeId);
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        let cancelled = false;
        http.get(GetLoginIdFormat)
            .then(({ data: body }) => {
                if (cancelled || body?.error) return;
                setEmpId(body?.data?.nextLoginId || '');
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        http.get(GetUserTypes)
            .then(({ data: body }) => {
                if (cancelled || body?.error) return;
                const list = Array.isArray(body?.data?.roles) ? body.data.roles : [];
                setRoles(list.filter((r) => canAssignRole(myUserTypeId, r.id)));
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [myUserTypeId]);

    const { departmentNames: DEPARTMENTS, designationNames: DESIGNATIONS } = useOrgOptions();

    const [form, setForm] = useState(EMPTY);
    const [photo, setPhoto] = useState(null);
    const [docs, setDocs] = useState({});
    const [snack, setSnack] = useState({ msg: '', sev: 'warning' });
    const [saving, setSaving] = useState(false);
    const [triedSubmit, setTriedSubmit] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);
    const photoInput = useRef(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const invalid = (k) => triedSubmit && MANDATORY.some((m) => m.key === k) && !String(form[k] || '').trim();

    const today = todayInput();
    const dobFuture = Boolean(form.dob) && form.dob > today;

    const prevJobs = form.previousEmployments;
    const setPrevJob = (idx, key) => (e) => setForm((f) => ({
        ...f,
        previousEmployments: f.previousEmployments.map((p, i) => (i === idx ? { ...p, [key]: e.target.value } : p)),
    }));
    const addPrevJob = () => setForm((f) => (f.previousEmployments.length >= MAX_PREV_JOBS
        ? f
        : { ...f, previousEmployments: [...f.previousEmployments, emptyPrevJob()] }));
    const removePrevJob = (idx) => setForm((f) => ({
        ...f,
        previousEmployments: f.previousEmployments.length <= 1
            ? f.previousEmployments
            : f.previousEmployments.filter((_, i) => i !== idx),
    }));

    const eduList = form.education;
    const setEdu = (idx, key) => (e) => setForm((f) => ({
        ...f,
        education: f.education.map((ed, i) => (i === idx ? { ...ed, [key]: e.target.value } : ed)),
    }));
    const addEdu = () => setForm((f) => (f.education.length >= MAX_EDU
        ? f
        : { ...f, education: [...f.education, emptyEducation()] }));
    const removeEdu = (idx) => setForm((f) => ({
        ...f,
        education: f.education.length <= 1 ? f.education : f.education.filter((_, i) => i !== idx),
    }));

    const doneCount = MANDATORY.filter((m) => String(form[m.key] || '').trim()).length;
    const totalMandatory = MANDATORY.length;
    const pct = Math.round((doneCount / totalMandatory) * 100);

    // Left-hand nav: the highlight changes only when an item is clicked, never while scrolling.
    const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

    const scrollTo = (id) => {
        setActiveSection(id);
        document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const pickPhoto = (e) => {
        const f = e.target.files?.[0];
        if (f) setPhoto({ url: URL.createObjectURL(f), name: f.name, file: f });
    };
    const pickDoc = (slug) => (e) => {
        const f = e.target.files?.[0];
        if (f) setDocs((d) => ({ ...d, [slug]: { url: URL.createObjectURL(f), name: f.name, file: f } }));
    };

    const submit = async () => {
        if (saving) return;
        setTriedSubmit(true);
        const missing = MANDATORY.filter((m) => !String(form[m.key] || '').trim());
        if (missing.length) {
            setSnack({ msg: `Please complete ${missing.length} required field${missing.length > 1 ? 's' : ''}: ${missing.slice(0, 3).map((m) => m.label).join(', ')}${missing.length > 3 ? '…' : ''}`, sev: 'error' });
            const firstSection = missing.some((m) => ['password'].includes(m.key)) ? 'login'
                : missing.some((m) => ['dateOfJoining', 'employmentType', 'department', 'designation'].includes(m.key)) && !missing.some((m) => ['firstName', 'lastName', 'gender', 'dob', 'personalMobile'].includes(m.key)) ? 'employment' : 'personal';
            scrollTo(firstSection);
            return;
        }
        if (txt(form.companyEmployeeId) && !COMPANY_ID_RE.test(txt(form.companyEmployeeId))) {
            setSnack({ msg: 'Employee ID must be 3–20 characters: letters, digits, dot, underscore or hyphen.', sev: 'error' });
            scrollTo('login');
            return;
        }
        if (txt(form.accountNumber) || txt(form.ifsc)) {
            const acc = txt(form.accountNumber);
            let bankErr = '';
            if (acc && !ACCOUNT_RE.test(acc)) bankErr = 'Account number must be 9–18 digits.';
            else if (acc && acc !== txt(form.confirmAccountNumber)) bankErr = 'Account numbers do not match.';
            else if (txt(form.ifsc) && !IFSC_RE.test(txt(form.ifsc).toUpperCase())) bankErr = 'Enter a valid IFSC code (e.g. HDFC0001234).';
            if (bankErr) { setSnack({ msg: bankErr, sev: 'error' }); scrollTo('bank'); return; }
        }
        if (dobFuture) {
            setSnack({ msg: 'Date of birth can’t be in the future.', sev: 'error' });
            scrollTo('personal');
            return;
        }
        if (txt(form.personalEmail) && !/^\S+@\S+\.\S+$/.test(String(form.personalEmail).trim())) {
            setSnack({ msg: 'Enter a valid email address.', sev: 'error' });
            scrollTo('personal');
            return;
        }
        if (form.password !== form.confirmPassword) {
            setSnack({ msg: 'Passwords do not match.', sev: 'error' });
            scrollTo('login');
            return;
        }

        setSaving(true);
        try {
            const { data: body } = await http.post(PostEmployee, buildEmployeePayload(form));
            if (body?.error) throw new Error(body.message || 'Could not onboard this employee.');

            const newId = body?.data?.employeeId ?? body?.data?.id ?? body?.employeeId;
            let employeeCode = body?.data?.employeeCode ?? body?.data?.code ?? body?.data?.loginId ?? '';
            let warning = '';
            if (newId == null) {
                warning = 'but the API returned no employee id, so no login was created';
            } else {
                try {
                    const { data: pwBody } = await http.post(SetEmployeeLoginPassword, {
                        employeeId: newId,
                        password: form.password,
                        confirmPassword: form.confirmPassword,
                    });
                    if (pwBody?.error) throw new Error(pwBody.message || 'password was rejected');

                    try {
                        if (!employeeCode) {
                            const { data: eb } = await http.get(GetEmployeeById, { params: { id: newId } });
                            employeeCode = eb?.data?.employeeCode ?? eb?.data?.code ?? '';
                        }
                        if (!employeeCode) throw new Error('employee code unavailable');
                        const { data: rtBody } = await http.put(UpdateEmployeeUserType, {
                            EmployeeCode: employeeCode,
                            UserTypeId: Number(form.userTypeId) || ROLE.EMPLOYEE,
                        });
                        if (rtBody?.error) throw new Error(rtBody.message || 'role was rejected');
                    } catch (rtErr) {
                        warning = `but their role wasn't assigned (${rtErr?.response || rtErr?.request ? apiErrorMessage(rtErr, 'unknown error') : rtErr.message}), so they can't sign in until it's set`;
                    }
                } catch (pwErr) {
                    warning = `but their password wasn't set (${pwErr?.response || pwErr?.request ? apiErrorMessage(pwErr, 'unknown error') : pwErr.message})`;
                }
            }

            if (newId != null) {
                const uploads = [];
                if (photo?.file) uploads.push({ slug: 'profile-photo', file: photo.file });
                Object.entries(docs).forEach(([slug, v]) => { if (v?.file) uploads.push({ slug, file: v.file }); });

                if (uploads.length) {
                    const results = await Promise.allSettled(uploads.map((u) => {
                        const fd = new FormData();
                        fd.append('employeeId', String(newId));
                        fd.append('documentType', u.slug);
                        fd.append('file', u.file);
                        return http.post(UploadEmployeeDoc, fd).then(({ data }) => { if (data?.error) throw new Error(data.message); });
                    }));
                    const failed = results.filter((r) => r.status === 'rejected').length;
                    if (failed > 0) {
                        const part = `${failed} document${failed > 1 ? 's' : ''} couldn't be uploaded`;
                        warning = warning ? `${warning}; ${part}` : `but ${part}`;
                    }
                }
            }

            const { confirmPassword, ...rest } = form;
            dispatch(addEmployee({
                employeeId: empId,
                ...rest,
                loginId: txt(form.companyEmployeeId) || employeeCode || empId,
                email: form.personalEmail,
                phone: form.personalMobile,
                doj: form.dateOfJoining,
                photoName: photo?.name || '',
                documents: Object.fromEntries(Object.entries(docs).map(([k, v]) => [k, v.name])),
            }));
            const who = `${form.firstName} ${form.lastName}`;
            navigate('/dashboard/employees', {
                state: warning
                    ? { toast: `${who} was onboarded, ${warning}. Set it from their profile.`, severity: 'warning' }
                    : { toast: `${who} onboarded successfully 🎉`, severity: 'success' },
            });
        } catch (err) {
            setSnack({
                msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not onboard this employee.') : err.message,
                sev: 'error',
            });
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 5, ...card, p: 2, mb: 1.5 }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <IconButton onClick={() => navigate('/dashboard/employees')} sx={{ bgcolor: '#F8FAFC', border: '1px solid #E6EAF1', borderRadius: '7px', '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <ArrowBackRoundedIcon sx={{ fontSize: 20, color: '#334155' }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>Onboard Employee</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.3 }}>Fill in the required sections, then add the employee.</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Box sx={{ mr: 0.5, width: 150, display: { xs: 'none', sm: 'block' } }}>
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                                <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>Required progress</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: pct === 100 ? '#16A34A' : PRIMARY }}>{doneCount}/{totalMandatory}</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 5, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: pct === 100 ? '#16A34A' : PRIMARY } }} />
                        </Box>
                        <Button disabled={saving} onClick={() => navigate('/dashboard/employees')} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600, borderRadius: '7px', height: 42, px: 2, border: '1px solid #E6EAF1' }}>Cancel</Button>
                        <Button disabled={saving} onClick={submit} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAddAlt1RoundedIcon />} sx={{ ...solidBtn, ...savingBtn, height: 42, px: 2.4 }}>{saving ? 'Adding…' : 'Add Employee'}</Button>
                    </Stack>
                </Stack>
            </Box>

            <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 3 }}>
                    <Box sx={{ ...card, p: 1, position: { md: 'sticky' }, top: { md: 96 } }}>
                        {SECTIONS.map((s) => {
                            const Icon = s.icon;
                            const done = s.id === 'login'
                                ? (Boolean(String(form.password || '').trim()) && form.password === form.confirmPassword)
                                : s.id === 'personal'
                                    ? ['firstName', 'lastName', 'gender', 'dob', 'personalMobile'].every((k) => String(form[k] || '').trim())
                                    : s.id === 'employment'
                                        ? ['dateOfJoining', 'employmentType', 'department', 'designation'].every((k) => String(form[k] || '').trim())
                                        : null;
                            const isActive = activeSection === s.id;
                            return (
                                <Stack key={s.id} direction="row" spacing={1.2} onClick={() => scrollTo(s.id)}
                                    aria-current={isActive ? 'true' : undefined}
                                    sx={{
                                        alignItems: 'center', px: 1.4, py: 1.1, borderRadius: '8px', cursor: 'pointer',
                                        color: isActive ? PRIMARY : '#475569',
                                        bgcolor: isActive ? PRIMARY_LIGHT : 'transparent',
                                        boxShadow: isActive ? `inset 3px 0 0 ${PRIMARY}` : 'none',
                                        transition: 'background-color .2s, color .2s, box-shadow .2s',
                                        '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY },
                                    }}>
                                    <Icon sx={{ fontSize: 18 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: isActive ? 800 : 600, flex: 1 }}>{s.label}</Typography>
                                    {s.required && (done
                                        ? <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                                        : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#E11D48' }} />)}
                                </Stack>
                            );
                        })}
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 9 }}>
                    <Stack spacing={1.5}>
                        <SectionCard section={SECTIONS[0]}>
                            <Box sx={{ p: 1.5, mb: 2.5, borderRadius: '8px', bgcolor: '#E4F0F9', border: '1px solid #DCEEFA', display: 'flex', alignItems: 'center', gap: 1.3 }}>
                                <VpnKeyRoundedIcon sx={{ fontSize: 18, color: PRIMARY, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, color: '#0E4A75', lineHeight: 1.5 }}>
                                    The <strong>Employee ID will be their login username</strong>. Set a password alongside it — the employee uses these two to sign in.
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Field label="Employee ID (company)" value={form.companyEmployeeId}
                                        onChange={(e) => setForm((f) => ({ ...f, companyEmployeeId: e.target.value.toUpperCase() }))}
                                        placeholder="e.g. SAF001"
                                        error={Boolean(form.companyEmployeeId) && !COMPANY_ID_RE.test(form.companyEmployeeId.trim())}
                                        helperText={`Optional · letters, digits . _ - · becomes the login username${empId ? ` · System code: ${empId} (generated)` : ''}`} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Label req>User Type (role)</Label>
                                    <TextField select fullWidth size="small" value={form.userTypeId} onChange={set('userTypeId')} sx={field}
                                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><AdminPanelSettingsRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }}
                                        helperText="Decides what they can access. Without a role, the account can't sign in.">
                                        {roles.length === 0
                                            ? <MenuItem value={ROLE.EMPLOYEE} sx={{ fontSize: 13.5 }}>Employee</MenuItem>
                                            : roles.map((r) => <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13.5 }}>{r.name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Label req>Password</Label>
                                    <TextField fullWidth size="small" type={showPwd ? 'text' : 'password'} placeholder="Set a password" value={form.password} onChange={set('password')} error={invalid('password')} sx={field}
                                        autoComplete="new-password"
                                        slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwd((v) => !v)} sx={{ p: 0.3 }}>{showPwd ? <VisibilityOffRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /> : <VisibilityRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />}</IconButton></InputAdornment> } }} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Label req>Confirm Password</Label>
                                    <TextField fullWidth size="small" type={showPwd2 ? 'text' : 'password'} placeholder="Re-enter password" value={form.confirmPassword} onChange={set('confirmPassword')}
                                        error={triedSubmit && String(form.confirmPassword || '') !== '' && form.password !== form.confirmPassword} sx={field}
                                        autoComplete="new-password"
                                        slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwd2((v) => !v)} sx={{ p: 0.3 }}>{showPwd2 ? <VisibilityOffRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /> : <VisibilityRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />}</IconButton></InputAdornment> } }} />
                                    {triedSubmit && form.confirmPassword && form.password !== form.confirmPassword && (
                                        <Typography sx={{ fontSize: 10.5, color: '#DC2626', mt: 0.4 }}>Passwords do not match</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[1]}>
                            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', p: 1.5, mb: 2.5, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6', flexWrap: 'wrap', gap: 1 }}>
                                <BadgeRoundedIcon sx={{ fontSize: 18, color: PRIMARY, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>Employee ID</Typography>
                                <Chip label={empId} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 800, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}` }} />
                                <Typography sx={{ fontSize: 11.5, color: '#9CA3AF', fontStyle: 'italic' }}>auto-generated · assigned on save</Typography>
                            </Stack>

                            <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar src={photo?.url} sx={{ width: 76, height: 76, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `2px solid ${PRIMARY_BORDER}` }}>
                                        {!photo && <PersonRoundedIcon sx={{ fontSize: 34 }} />}
                                    </Avatar>
                                    <IconButton onClick={() => photoInput.current?.click()} size="small" sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: PRIMARY, color: '#fff', width: 28, height: 28, '&:hover': { bgcolor: '#0E4A75' } }}>
                                        <PhotoCameraRoundedIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                    <input ref={photoInput} type="file" accept="image/*" hidden onChange={pickPhoto} />
                                </Box>
                                <Box>
                                    <Label>Profile Photo</Label>
                                    <Typography sx={{ fontSize: 12, color: '#94A3B8', mb: 0.8 }}>{photo ? photo.name : 'PNG or JPG, up to 5 MB · optional'}</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button onClick={() => photoInput.current?.click()} startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />} size="small" sx={{ ...tonalBtn, height: 32 }}>{photo ? 'Change' : 'Upload'}</Button>
                                        {photo && <Button onClick={() => setPhoto(null)} size="small" sx={{ color: '#DC2626', textTransform: 'none', fontWeight: 600, height: 32 }}>Remove</Button>}
                                    </Stack>
                                </Box>
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="First Name" req value={form.firstName} onChange={set('firstName')} error={invalid('firstName')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Last Name" req value={form.lastName} onChange={set('lastName')} error={invalid('lastName')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Gender" req value={form.gender} onChange={set('gender')} options={GENDERS} error={invalid('gender')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Label>Email</Label>
                                    <TextField
                                        fullWidth size="small" type="email" placeholder="name@email.com"
                                        value={form.personalEmail} onChange={set('personalEmail')} sx={field} autoComplete="off"
                                        error={Boolean(txt(form.personalEmail)) && !/^\S+@\S+\.\S+$/.test(String(form.personalEmail).trim())}
                                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><MailOutlineRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment> } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Date of Birth" req type="date" value={form.dob} onChange={set('dob')} max={today} error={invalid('dob') || dobFuture} helperText={dobFuture ? 'Date of birth can’t be in the future' : undefined} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Marital Status" value={form.maritalStatus} onChange={set('maritalStatus')} options={MARITAL} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Blood Group" value={form.bloodGroup} onChange={set('bloodGroup')} options={BLOOD} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Nationality" value={form.nationality} onChange={set('nationality')} placeholder="Indian" /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Personal Mobile Number" req value={form.personalMobile} onChange={set('personalMobile')} error={invalid('personalMobile')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Alternate Mobile Number" value={form.alternateMobile} onChange={set('alternateMobile')} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[2]}>
                            <Label req>Employment Type</Label>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {EMP_TYPES.map((t) => {
                                    const on = form.employmentType === t;
                                    return (
                                        <Box key={t} onClick={() => setForm((f) => ({ ...f, employmentType: t, ...(t !== 'Probation' ? { probationPeriod: '' } : {}) }))}
                                            sx={{ px: 1.8, py: 0.9, borderRadius: '7px', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, userSelect: 'none',
                                                border: '1px solid', borderColor: on ? PRIMARY : (invalid('employmentType') ? '#FCA5A5' : '#E5E7EB'),
                                                bgcolor: on ? PRIMARY_LIGHT : '#F8FAFC', color: on ? PRIMARY : '#64748B', transition: 'all .15s',
                                                '&:hover': { borderColor: PRIMARY } }}>
                                            {t}
                                        </Box>
                                    );
                                })}
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Date of Joining" req type="date" value={form.dateOfJoining} onChange={set('dateOfJoining')} error={invalid('dateOfJoining')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Department" req value={form.department} onChange={set('department')} options={DEPARTMENTS} error={invalid('department')} emptyText="No departments to select — add one under Organisation → Departments" /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Designation" req value={form.designation} onChange={set('designation')} options={DESIGNATIONS} error={invalid('designation')} emptyText="No designations to select — add one under Organisation → Designations" /></Grid>
                                {form.employmentType === 'Probation' && (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Probation Period (months)" type="number" value={form.probationPeriod} onChange={set('probationPeriod')} /></Grid>
                                )}
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Confirmation Date" type="date" value={form.confirmationDate} onChange={set('confirmationDate')} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[3]}>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.4 }}>Present (Current) Address</Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Address Line 1" value={form.presentAddressLine1} onChange={set('presentAddressLine1')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Address Line 2" value={form.presentAddressLine2} onChange={set('presentAddressLine2')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="City" value={form.presentCity} onChange={set('presentCity')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="State" value={form.presentState} onChange={set('presentState')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Country" value={form.presentCountry} onChange={set('presentCountry')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Postal Code" value={form.presentPostalCode} onChange={set('presentPostalCode')} /></Grid>
                            </Grid>

                            <Box sx={{ mt: 2.2, pt: 2, borderTop: '1px solid #F1F3F7' }}>
                                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.4, flexWrap: 'wrap', gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>Permanent Address</Typography>
                                    <Box
                                        onClick={() => setForm((f) => ({ ...f, sameAsPresent: !f.sameAsPresent }))}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, cursor: 'pointer', userSelect: 'none' }}
                                    >
                                        <Box sx={{ width: 18, height: 18, borderRadius: '5px', border: `1.5px solid ${form.sameAsPresent ? PRIMARY : '#CBD5E1'}`, bgcolor: form.sameAsPresent ? PRIMARY : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                                            {form.sameAsPresent && <CheckRoundedIcon sx={{ fontSize: 13, color: '#fff' }} />}
                                        </Box>
                                        <Typography sx={{ fontSize: 12.5, color: '#475569', fontWeight: 600 }}>Same as present address</Typography>
                                    </Box>
                                </Stack>

                                {form.sameAsPresent ? (
                                    <Box sx={{ p: 1.4, borderRadius: '8px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                                        <Typography sx={{ fontSize: 12.5, color: '#166534' }}>Permanent address will be saved the same as the present address above.</Typography>
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}><Field label="Address Line 1" value={form.permanentAddressLine1} onChange={set('permanentAddressLine1')} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}><Field label="Address Line 2" value={form.permanentAddressLine2} onChange={set('permanentAddressLine2')} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="City" value={form.permanentCity} onChange={set('permanentCity')} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="State" value={form.permanentState} onChange={set('permanentState')} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Country" value={form.permanentCountry} onChange={set('permanentCountry')} /></Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Postal Code" value={form.permanentPostalCode} onChange={set('permanentPostalCode')} /></Grid>
                                    </Grid>
                                )}
                            </Box>
                        </SectionCard>

                        <SectionCard section={SECTIONS[4]}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Aadhaar Number" value={form.aadhaar} onChange={set('aadhaar')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="PAN Number" value={form.pan} onChange={set('pan')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Passport Number" value={form.passport} onChange={set('passport')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Driving License" value={form.drivingLicense} onChange={set('drivingLicense')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="Voter ID" value={form.voterId} onChange={set('voterId')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="UAN Number" value={form.uan} onChange={set('uan')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="ESI Number" value={form.esi} onChange={set('esi')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}><Field label="PF Number" value={form.pf} onChange={set('pf')} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[5]}>
                            <Stack spacing={1.6}>
                                {eduList.map((edu, idx) => (
                                    <Box key={idx} sx={{ position: 'relative', p: 1.8, pt: 2, borderRadius: '9px', border: '1px solid #EEF1F6', bgcolor: '#F3F8FC' }}>
                                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.4 }}>
                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontSize: 11.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</Box>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{edu.level ? edu.level : `Qualification ${idx + 1}`}</Typography>
                                            </Stack>
                                            {eduList.length > 1 && (
                                                <Tooltip arrow title="Remove this qualification">
                                                    <IconButton size="small" onClick={() => removeEdu(idx)} sx={{ color: '#94A3B8', border: '1px solid #EEF1F6', borderRadius: '7px', width: 28, height: 28, '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2', borderColor: '#FECACA' } }}>
                                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Select label="Qualification Level" value={edu.level} onChange={setEdu(idx, 'level')} options={EDU_LEVELS} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Degree / Course" placeholder="e.g. B.Tech, B.Com, SSLC" value={edu.degree} onChange={setEdu(idx, 'degree')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Specialization / Stream" placeholder="e.g. Computer Science" value={edu.specialization} onChange={setEdu(idx, 'specialization')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="School / College" value={edu.institution} onChange={setEdu(idx, 'institution')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Board / University" value={edu.board} onChange={setEdu(idx, 'board')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><Field label="Year of Passing" value={edu.yearOfPassing} onChange={setEdu(idx, 'yearOfPassing')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><Field label="Percentage / CGPA" value={edu.percentage} onChange={setEdu(idx, 'percentage')} /></Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </Stack>

                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.6, flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                    onClick={addEdu}
                                    disabled={eduList.length >= MAX_EDU}
                                    startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                                    sx={{ ...tonalBtn, height: 38, px: 1.8, fontSize: 12.5, '&.Mui-disabled': { opacity: 0.5 } }}
                                >
                                    Add qualification
                                </Button>
                                <Typography sx={{ fontSize: 11, color: eduList.length >= MAX_EDU ? '#DC2626' : '#98A0AE', fontWeight: 600 }}>
                                    {eduList.length} / {MAX_EDU}{eduList.length >= MAX_EDU ? ' · limit reached' : ''}
                                </Typography>
                            </Stack>

                            <Box sx={{ mt: 1.8, pt: 1.8, borderTop: '1px solid #F1F3F7' }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}><Field label="Certifications (optional)" placeholder="Any professional certifications, comma-separated" value={form.certifications} onChange={set('certifications')} /></Grid>
                                </Grid>
                            </Box>
                        </SectionCard>

                        <SectionCard section={SECTIONS[6]}>
                            <Stack spacing={1.6}>
                                {prevJobs.map((job, idx) => (
                                    <Box key={idx} sx={{ position: 'relative', p: 1.8, pt: 2, borderRadius: '9px', border: '1px solid #EEF1F6', bgcolor: '#F3F8FC' }}>
                                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.4 }}>
                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontSize: 11.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</Box>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>Previous employer {idx + 1}</Typography>
                                            </Stack>
                                            {prevJobs.length > 1 && (
                                                <Tooltip arrow title="Remove this employer">
                                                    <IconButton size="small" onClick={() => removePrevJob(idx)} sx={{ color: '#94A3B8', border: '1px solid #EEF1F6', borderRadius: '7px', width: 28, height: 28, '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2', borderColor: '#FECACA' } }}>
                                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Company Name" value={job.company} onChange={setPrevJob(idx, 'company')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Designation" value={job.designation} onChange={setPrevJob(idx, 'designation')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Experience (years)" value={job.experience} onChange={setPrevJob(idx, 'experience')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Start Date" type="date" value={job.startDate} onChange={setPrevJob(idx, 'startDate')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="End Date" type="date" value={job.endDate} onChange={setPrevJob(idx, 'endDate')} /></Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Last Drawn Salary (CTC)" value={job.lastDrawnSalary} onChange={setPrevJob(idx, 'lastDrawnSalary')} /></Grid>
                                            <Grid size={{ xs: 12 }}><Field label="Reason for Leaving" value={job.reasonForLeaving} onChange={setPrevJob(idx, 'reasonForLeaving')} /></Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </Stack>

                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.6, flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                    onClick={addPrevJob}
                                    disabled={prevJobs.length >= MAX_PREV_JOBS}
                                    startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                                    sx={{ ...tonalBtn, height: 38, px: 1.8, fontSize: 12.5, '&.Mui-disabled': { opacity: 0.5 } }}
                                >
                                    Add previous employer
                                </Button>
                                <Typography sx={{ fontSize: 11, color: prevJobs.length >= MAX_PREV_JOBS ? '#DC2626' : '#98A0AE', fontWeight: 600 }}>
                                    {prevJobs.length} / {MAX_PREV_JOBS}{prevJobs.length >= MAX_PREV_JOBS ? ' · limit reached' : ''}
                                </Typography>
                            </Stack>
                        </SectionCard>

                        <SectionCard section={SECTIONS[7]}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Contact Name" value={form.emergencyName} onChange={set('emergencyName')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Relationship" value={form.emergencyRelationship} onChange={set('emergencyRelationship')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Mobile Number" value={form.emergencyMobile} onChange={set('emergencyMobile')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}><Field label="Alternate Number" value={form.emergencyAlternate} onChange={set('emergencyAlternate')} /></Grid>
                                <Grid size={{ xs: 12, sm: 8 }}><Field label="Address" value={form.emergencyAddress} onChange={set('emergencyAddress')} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[8]}>
                            <Typography sx={{ fontSize: 12.5, color: '#64748B', mb: 1.5 }}>Used to credit salary. All fields are optional now and can be filled in later.</Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Account Holder Name" value={form.accountHolderName} onChange={set('accountHolderName')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Bank Name" value={form.bankName} onChange={set('bankName')} /></Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Field label="Account Number" value={form.accountNumber}
                                        onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                                        error={Boolean(form.accountNumber) && !ACCOUNT_RE.test(form.accountNumber)}
                                        helperText={form.accountNumber && !ACCOUNT_RE.test(form.accountNumber) ? '9–18 digits' : undefined} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Field label="Confirm Account Number" value={form.confirmAccountNumber}
                                        onChange={(e) => setForm((f) => ({ ...f, confirmAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                                        error={Boolean(form.confirmAccountNumber) && form.confirmAccountNumber !== form.accountNumber}
                                        helperText={form.confirmAccountNumber && form.confirmAccountNumber !== form.accountNumber ? 'Does not match' : undefined} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Field label="IFSC Code" value={form.ifsc} placeholder="HDFC0001234"
                                        onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) }))}
                                        error={Boolean(form.ifsc) && !IFSC_RE.test(form.ifsc)}
                                        helperText={form.ifsc && !IFSC_RE.test(form.ifsc) ? '4 letters, 0, then 6 letters/digits' : undefined} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}><Field label="Branch" value={form.branch} onChange={set('branch')} /></Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard section={SECTIONS[9]}>
                            <Typography sx={{ fontSize: 12.5, color: '#64748B', mb: 1.5 }}>Upload any supporting documents. You can download them back anytime.</Typography>
                            <Grid container spacing={1.5}>
                                {DOC_ITEMS.map((item) => {
                                    const doc = docs[item.slug];
                                    return (
                                        <Grid size={{ xs: 12, sm: 6 }} key={item.slug}>
                                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.3, borderRadius: '7px', border: '1px solid #EEF1F6', bgcolor: doc ? PRIMARY_LIGHT : '#F8FAFC' }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }} noWrap>{item.label}</Typography>
                                                    <Typography sx={{ fontSize: 10.5, color: doc ? PRIMARY : '#9CA3AF' }} noWrap>{doc ? doc.name : 'No file chosen'}</Typography>
                                                </Box>
                                                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                                    {doc && (
                                                        <Tooltip arrow title="Preview">
                                                            <IconButton size="small" component="a" href={doc.url} download={doc.name} sx={{ bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', width: 30, height: 30 }}>
                                                                <DownloadRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {doc ? (
                                                        <Tooltip arrow title="Remove">
                                                            <IconButton size="small" onClick={() => setDocs((x) => { const n = { ...x }; delete n[item.slug]; return n; })} sx={{ bgcolor: '#fff', border: '1px solid #FECACA', borderRadius: '7px', width: 30, height: 30 }}>
                                                                <CloseRoundedIcon sx={{ fontSize: 15, color: '#DC2626' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Button component="label" size="small" startIcon={<UploadFileRoundedIcon sx={{ fontSize: 15 }} />} sx={{ ...tonalBtn, height: 30, fontSize: 11.5, px: 1.2 }}>
                                                            Choose
                                                            <input type="file" hidden onChange={pickDoc(item.slug)} />
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 1.5 }}>Files are uploaded to the employee's record right after onboarding.</Typography>
                        </SectionCard>

                        <Box sx={{ ...card, p: 2 }}>
                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                                <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>
                                    Only the <strong style={{ color: '#E11D48' }}>required</strong> fields are needed. Fill the rest now or update later.
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button disabled={saving} onClick={() => navigate('/dashboard/employees')} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600, borderRadius: '7px', height: 42, px: 2, border: '1px solid #E6EAF1' }}>Cancel</Button>
                                    <Button disabled={saving} onClick={submit} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAddAlt1RoundedIcon />} sx={{ ...solidBtn, ...savingBtn, height: 42, px: 2.4 }}>{saving ? 'Adding…' : 'Add Employee'}</Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={4000} onClose={() => setSnack({ msg: '', sev: 'warning' })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack({ msg: '', sev: 'warning' })} severity={snack.sev} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
